<script setup lang="ts">
import BaseTabs from "@/components/ui/BaseTabs.vue";
import {useUserStore, useUserFilesStore, useProfileStore, useEventsStore, useApplicationsStore, useAssignmentsStore, useRatingStore, useRecommendationStore, useDashboardStore} from "@/stores";
import {storeToRefs} from "pinia";

import {useAdminHandler} from "@/composables/useAdminHandler";

const dashboardStore = useDashboardStore();
const { latestData } = storeToRefs(dashboardStore);

const userStore = useUserStore();
const { currentUser, users } = storeToRefs(userStore);

const userFilesStore = useUserFilesStore();
const { currentFiles } = storeToRefs(userFilesStore);

const profileStore = useProfileStore();
const { currentProfile, profiles, profile } = storeToRefs(profileStore);
const eventsStore = useEventsStore();
const { currentEvent, myEvents, events } = storeToRefs(eventsStore);
const applicationsStore = useApplicationsStore();
const { currentApplication, applications, eventApplications, eventApplicants } = storeToRefs(applicationsStore);
const assignmentsStore = useAssignmentsStore();
const { currentAssignment, assignments, eventAssignment } = storeToRefs(assignmentsStore);
const ratingStore = useRatingStore();
const { currentRating, rating, myRatingsReceived, myRatingsGiven, ratings } = storeToRefs(ratingStore);

const recommendationStore = useRecommendationStore();
const { currentRecommendation, recommendation, recommendations } = storeToRefs(recommendationStore);

const { confirmCron, handlingInProgress, confirmReset, confirmPush } = useAdminHandler();

</script>

<template>
  <BaseTabs :tabs="[
    { label: 'Overview', value: 'overview' },
    { label: 'Dashboard', value: 'dashboard' },
    { label: 'User', value: 'user' },
    { label: 'Profile', value: 'profile' },
    { label: 'Events', value: 'events' },
    { label: 'Applications', value: 'applications' },
    { label: 'Assignments', value: 'assignments' },
    { label: 'Ratings', value: 'ratings' },
    { label: 'Recommendations', value: 'recommendations' }
  ]">
    <template #default="{ current }">
      <div v-if="current === 'overview'">
        <h1>Overview</h1>

        <h2>CronJobs</h2>
        <div class="flex flex-wrap gap-4 mt-4">
          <button
            @click="confirmCron('users')"
            class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            🧹 Delete Users
          </button>

          <button
            @click="confirmCron('events')"
            class="bg-orange-600 hover:bg-orange-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            🗓️ Delete Events
          </button>

          <button
            @click="confirmCron('assignments')"
            class="bg-yellow-600 hover:bg-yellow-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            🗑️ Delete Assignments
          </button>
        </div>

        <h2>Tools</h2>
        <div class="flex flex-wrap gap-4 mt-4">
          <button
            @click="confirmReset()"
            class="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            🧹 RESET DB
          </button>

          <button
            @click="confirmPush()"
            class="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded shadow transition"
          >
            Send Push Notifications
          </button>
        </div>


      </div>
      <div v-if="current === 'dashboard'">
        <h1>Dashboard</h1>
        <h2>latestData</h2>
        <pre>{{ latestData }}</pre>
      </div>

      <div v-if="current === 'user'">
        <h1>User</h1>
        <h2>currentUser</h2>
        <pre>{{ currentUser }}</pre>

        <h2>currentFiles</h2>
        <pre>{{ currentFiles }}</pre>

        <h2>Users</h2>
        <pre>{{ users }}</pre>
      </div>

      <div v-if="current === 'profile'">
        <h1>Profile</h1>
        <h2>currentProfile</h2>
        <pre>{{ currentProfile }}</pre>

        <h2>profile</h2>
        <pre>{{ profile }}</pre>

        <h2>profiles</h2>
        <pre>{{ profiles }}</pre>
      </div>

      <div v-if="current === 'events'">
        <h1>Events</h1>

        <h2>currentEvent</h2>
        <pre>{{ currentEvent }}</pre>

        <h2>myEvents</h2>
        <pre>{{ myEvents }}</pre>

        <h2>events</h2>
        <pre>{{ events }}</pre>
      </div>

      <div v-if="current === 'applications'">
        <h1>Applications</h1>

        <h2>currentApplication</h2>
        <pre>{{ currentApplication }}</pre>

        <h2>applications</h2>
        <pre>{{ applications }}</pre>

        <h2>eventApplications</h2>
        <pre>{{ eventApplications }}</pre>

        <h2>eventApplicants</h2>
        <pre>{{ eventApplicants }}</pre>

      </div>

      <div v-if="current === 'assignments'">
        <h1>Assignments</h1>

        <h2>currentAssignment</h2>
        <pre>{{ currentAssignment }}</pre>

        <h2>assignments</h2>
        <pre>{{ assignments }}</pre>

        <h2>eventAssignment</h2>
        <pre>{{ eventAssignment }}</pre>
      </div>

      <div v-if="current === 'ratings'">

        <h1>Ratings</h1>

        <h2>currentRating</h2>
        <pre>{{ currentRating }}</pre>

        <h2>rating</h2>
        <pre>{{ rating }}</pre>

        <h2>myRatingsReceived</h2>
        <pre>{{ myRatingsReceived }}</pre>

        <h2>myRatingsGiven</h2>
        <pre>{{ myRatingsGiven }}</pre>

        <h2>ratings</h2>
        <pre>{{ ratings }}</pre>
      </div>

      <div v-if="current === 'recommendations'">

        <h1>Recommendations</h1>

        <h2>currentRecommendations</h2>
        <pre>{{ currentRecommendation }}</pre>

        <h2>recommendation</h2>
        <pre>{{ recommendation }}</pre>

        <h2>recommendations</h2>
        <pre>{{ recommendations }}</pre>
      </div>


    </template>
  </BaseTabs>
</template>

<style scoped>
h1 {
  font-size: 1.5rem;
  font-weight: bold;
}
h2 {
  margin-top: 1rem;
  font-size: 1rem;
  font-weight: bold;
}
</style>
